import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, Plus, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200">
      <div className="px-6 py-3 border-b border-slate-200">
        <h3 className="text-sm font-bold tracking-widest text-slate-900 uppercase">{title}</h3>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, testId, placeholder, mono = false, full = false }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="coord-label">{label}</Label>
      <Input
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1.5 rounded-sm ${mono ? "font-mono" : ""}`}
        placeholder={placeholder}
      />
    </div>
  );
}

export default function ResultadosTab({ exp, setExp }) {
  const { t } = useLang();
  const sr = exp.survey_results || {};
  const upd = (field, val) =>
    setExp({ ...exp, survey_results: { ...exp.survey_results, [field]: val } });

  // ----- Resultantes (lista dinámica) -----
  // Normalize: if legacy fields exist and list is empty, seed from legacy
  const resultantes = (() => {
    if (Array.isArray(sr.resultantes) && sr.resultantes.length > 0) return sr.resultantes;
    const legacy = [];
    if (sr.parcela_resultante) legacy.push({ parcela: sr.parcela_resultante, area: sr.area_resultante || "" });
    if (sr.parcela_resultante_2) legacy.push({ parcela: sr.parcela_resultante_2, area: sr.area_resultante_2 || "" });
    return legacy;
  })();

  const setResultantes = (next) => {
    setExp({ ...exp, survey_results: { ...exp.survey_results, resultantes: next } });
  };

  const addResultante = () => setResultantes([...resultantes, { parcela: "", area: "" }]);
  const removeResultante = (i) => setResultantes(resultantes.filter((_, idx) => idx !== i));
  const updResultante = (i, field, val) => {
    const next = resultantes.map((r, idx) => (idx === i ? { ...r, [field]: val } : r));
    setResultantes(next);
  };

  const autoParcelas = () => {
    const num = (exp.numero_expediente || "").trim();
    if (num.length <= 2) {
      toast.error("Número de expediente muy corto");
      return;
    }
    const base = num.slice(2);
    const count = resultantes.length === 0 ? 2 : resultantes.length;
    const next = Array.from({ length: count }, (_, i) => ({
      parcela: `${base}_1_${i + 1}`,
      area: resultantes[i]?.area || "",
    }));
    setResultantes(next);
    toast.success(`${count} designaciones generadas`);
  };

  return (
    <div className="space-y-6" data-testid="resultados-tab">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">{t.results_section_title}</h2>
        <p className="text-slate-600 mt-1">{t.results_section_desc}</p>
      </div>

      <Section title="Fechas / Horarios">
        <Field label={t.sr_fecha_autorizacion} value={sr.fecha_autorizacion} onChange={(v) => upd("fecha_autorizacion", v)} testId="sr-fecha-aut" mono placeholder="DD/MM/YYYY" />
        <Field label={t.sr_fecha_medicion} value={sr.fecha_medicion} onChange={(v) => upd("fecha_medicion", v)} testId="sr-fecha-med" mono placeholder="DD/MM/YYYY" />
        <Field label={t.sr_hora_inicio} value={sr.hora_inicio} onChange={(v) => upd("hora_inicio", v)} testId="sr-hora-ini" mono placeholder="10:00 a.m." />
        <Field label={t.sr_hora_fin} value={sr.hora_fin} onChange={(v) => upd("hora_fin", v)} testId="sr-hora-fin" mono placeholder="01:30 p.m." />
      </Section>

      <div className="bg-white border border-slate-200">
        <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold tracking-widest text-slate-900 uppercase">Resultantes ({resultantes.length})</h3>
          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="sr-auto-parcelas-btn"
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={autoParcelas}
            >
              <ArrowsClockwise size={14} className="mr-1" /> {t.sr_auto}
            </Button>
            <Button
              type="button"
              data-testid="sr-add-resultante"
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={addResultante}
            >
              <Plus size={14} className="mr-1" /> Agregar
            </Button>
          </div>
        </div>
        <div className="p-6 space-y-3">
          {resultantes.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6">
              No hay resultantes. Pulsa <b>Auto-completar</b> para generarlas desde el No. de Expediente, o <b>Agregar</b> para añadir una manual.
            </div>
          ) : (
            resultantes.map((r, i) => (
              <div key={i} className="flex items-end gap-2" data-testid={`sr-resultante-${i}`}>
                <div className="w-10 pb-2 font-mono text-xs text-slate-500 text-center">#{i + 1}</div>
                <div className="flex-1">
                  <Label className="coord-label">Parcela {i + 1}</Label>
                  <Input
                    data-testid={`sr-resultante-${i}-parcela`}
                    value={r.parcela || ""}
                    onChange={(e) => updResultante(i, "parcela", e.target.value)}
                    className="mt-1.5 rounded-sm font-mono"
                    placeholder="42025023807_1_1"
                  />
                </div>
                <div className="w-48">
                  <Label className="coord-label">Área</Label>
                  <Input
                    data-testid={`sr-resultante-${i}-area`}
                    value={r.area || ""}
                    onChange={(e) => updResultante(i, "area", e.target.value)}
                    className="mt-1.5 rounded-sm font-mono"
                    placeholder="0.00 m²"
                  />
                </div>
                <Button
                  type="button"
                  data-testid={`sr-remove-resultante-${i}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => removeResultante(i)}
                  className="rounded-sm text-slate-400 hover:text-red-600"
                >
                  <Minus size={14} />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      <Section title="Áreas globales">
        <Field label={t.sr_area_titulo} value={sr.area_segun_titulo} onChange={(v) => upd("area_segun_titulo", v)} testId="sr-area-tit" mono placeholder="0.00 m²" />
        <Field label={t.sr_area_derecho} value={sr.area_derecho} onChange={(v) => upd("area_derecho", v)} testId="sr-area-der" mono placeholder="0.00 m²" />
        <Field label={t.sr_area_presentada} value={sr.area_presentada} onChange={(v) => upd("area_presentada", v)} testId="sr-area-pres" mono placeholder="0.00 m²" />
        <Field label={t.sr_diferencia} value={sr.diferencia_area} onChange={(v) => upd("diferencia_area", v)} testId="sr-diferencia" mono placeholder="(-) 0.25 m² por defecto" />
      </Section>

      <Section title="Método de levantamiento">
        <Field label={t.sr_metodo_levantamiento} value={sr.metodo_levantamiento} onChange={(v) => upd("metodo_levantamiento", v)} testId="sr-metodo-lev" placeholder="Sencillo / Repetitivo / Directo / Inverso" />
        <Field label={t.sr_instrumento} value={sr.instrumento} onChange={(v) => upd("instrumento", v)} testId="sr-instrumento" placeholder="Marca y modelo (o usar el del Perfil)" />
        <Field label={t.sr_metodo_orientacion} value={sr.metodo_orientacion} onChange={(v) => upd("metodo_orientacion", v)} testId="sr-metodo-ori" full />
      </Section>

      <div className="bg-white border border-slate-200 p-6">
        <Label className="coord-label">{t.sr_observaciones}</Label>
        <Textarea
          data-testid="sr-observaciones"
          value={sr.observaciones || ""}
          onChange={(e) => upd("observaciones", e.target.value)}
          className="mt-1.5 rounded-sm min-h-[140px]"
        />
      </div>
    </div>
  );
}
